import { privateApiClient, publicApiClient } from './apiClient';

/**
 * Service pour gérer les organisations
 */

/**
 * Récupérer les catégories pour création d'organisation
 * @param {string} solutionId - ID de la solution (ex: POS)
 * @returns {Promise<Array>} Liste des catégories
 */
export const fetchOrganizationCategories = async (solutionId = 'POS') => {
    const { data } = await publicApiClient.get('/usr/categories', {
        params: {
            pge: 1,
            sol: solutionId,
            grn: 1,
            prt: 'all',
            lmt: 100,
            slug: ''
        }
    });
    
    return data.contents || [];
};

/**
 * Créer une nouvelle organisation
 * @param {Object} organizationData - Données de l'organisation
 * @returns {Promise<Object>} Organisation créée (payload de la réponse)
 */
export const createOrganization = async (organizationData) => {
    const response = await privateApiClient.post('/usr/companies', organizationData);
    const data = response?.data;
    
    if (!data) {
        throw new Error('Aucune réponse de l\'API');
    }
    
    if (data.error) {
        throw new Error(data.message || 'Erreur lors de la création de l\'organisation');
    }
    
    // Côté mobile, ils utilisent data.payload (ligne 93 de AddOrgScreen.js)
    // Retourner payload si disponible, sinon data directement
    return data.payload || data;
};

/**
 * Récupérer une organisation par tag
 * @param {string} tag - Tag de l'organisation
 * @returns {Promise<Object>} Organisation
 */
export const fetchOrganizationByTag = async (tag) => {
    const { data } = await publicApiClient.get('/usr/companies', {
        params: { tag }
    });
    
    return data;
};

/**
 * Récupérer la liste des organisations
 * @param {Object} params - Paramètres de recherche { pge, slug, cat, sol, lmt }
 * @returns {Promise<Object>} { contents: Array, nextPage: number, previousPage: number }
 */
export const fetchOrganizations = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        slug: '',
        cat: 'all',
        sol: '',
        lmt: 20,
        ...params
    };
    
    const { data } = await privateApiClient.get('/usr/companies', {
        params: defaultParams
    });
    
    return {
        contents: data?.contents || [],
        nextPage: data?.nextPage || null,
        previousPage: data?.previousPage || null
    };
};

/**
 * Récupérer une organisation par ID (détails complets)
 * @param {string} organizationId - ID de l'organisation
 * @returns {Promise<Object|null>} Organisation ou null
 */
export const fetchOrganizationById = async (organizationId) => {
    if (!organizationId) return null;
    try {
        const { data } = await privateApiClient.get('/usr/companies', {
            params: { pge: 1, lmt: 1, ids: organizationId }
        });
        const list = data?.contents || [];
        return list.find((c) => c.id === organizationId) || list[0] || null;
    } catch (e) {
        console.warn('[fetchOrganizationById]', e);
        return null;
    }
};

/**
 * Mettre à jour une organisation (name, rccm, nif, idnat, description, category_id - comme mobile)
 * @param {string} organizationId - ID de l'organisation
 * @param {Object} organizationData - Données à mettre à jour
 * @returns {Promise<Object>} Organisation mise à jour
 */
export const updateOrganization = async (organizationId, organizationData) => {
    const { data } = await privateApiClient.put(`/usr/companies/${organizationId}`, organizationData);
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la mise à jour de l\'organisation');
    }
    
    return data?.contents ?? data?.payload ?? data;
};

/**
 * Ajouter un contact à une organisation
 * @param {string} organizationId - ID de l'organisation
 * @param {Object} contactData - Données du contact { zip, phone, email, official_web_site }
 * @returns {Promise<Object>} Contact créé
 */
export const addOrganizationContact = async (organizationId, contactData) => {
    const response = await privateApiClient.post(`/usr/companies/${organizationId}/contacts`, contactData);
    const data = response?.data;
    
    if (!data) {
        throw new Error('Aucune réponse de l\'API');
    }
    
    // Côté mobile (ligne 108-109 de AddOrgScreen.js), ils vérifient data.error
    if (data.error) {
        throw new Error(data.message || 'Erreur lors de l\'ajout du contact');
    }
    
    // Côté mobile, ils utilisent directement data (pas de payload pour le contact)
    return data;
};